"use client";

import { cn } from "@newxsystems/shared";
import { NavigationMenu as NavigationMenuPrimitive } from "radix-ui";
import type * as React from "react";

const navigationMenuTriggerClassName =
  "group inline-flex h-10 w-max cursor-pointer items-center justify-center rounded-full border border-transparent bg-transparent px-4 py-2 text-sm font-medium tracking-[-0.01em] text-foreground transition-[background-color,border-color,color,box-shadow] duration-200 hover:border-border hover:bg-surface-100 hover:text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35 disabled:pointer-events-none disabled:opacity-50 data-[state=open]:border-border data-[state=open]:bg-surface-100 data-[state=open]:shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--color-border)_55%,transparent),0_1px_0_rgba(17,17,17,0.04)]";

const NavigationMenuChevron = () => (
  <svg
    aria-hidden="true"
    className="size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180 group-open:rotate-180"
    fill="none"
    viewBox="0 0 16 16"
  >
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function NavigationMenu({
  className,
  children,
  viewport = false,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root> & { viewport?: boolean }) {
  return (
    <NavigationMenuPrimitive.Root
      className={cn(
        "group/navigation-menu relative flex max-w-max flex-1 items-center justify-center",
        className,
      )}
      data-slot="navigation-menu"
      data-viewport={viewport}
      {...props}
    >
      {children}
      {viewport ? <NavigationMenuViewport /> : null}
    </NavigationMenuPrimitive.Root>
  );
}

function NavigationMenuList({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
  return (
    <NavigationMenuPrimitive.List
      className={cn("group flex flex-1 list-none items-center justify-center gap-1", className)}
      data-slot="navigation-menu-list"
      {...props}
    />
  );
}

function NavigationMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) {
  return (
    <NavigationMenuPrimitive.Item
      className={cn("relative", className)}
      data-slot="navigation-menu-item"
      {...props}
    />
  );
}

function NavigationMenuTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger>) {
  return (
    <NavigationMenuPrimitive.Trigger
      className={cn(navigationMenuTriggerClassName, className)}
      data-slot="navigation-menu-trigger"
      {...props}
    >
      {children}
      <NavigationMenuChevron />
    </NavigationMenuPrimitive.Trigger>
  );
}

function NavigationMenuContent({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Content>) {
  return (
    <NavigationMenuPrimitive.Content
      className={cn(
        "left-1/2 top-full z-50 mt-3 w-max -translate-x-1/2 transition-[opacity,transform] duration-200 data-[motion=from-end]:translate-x-[calc(-50%+0.75rem)] data-[motion=from-start]:translate-x-[calc(-50%-0.75rem)] data-[motion^=from-]:opacity-100 data-[motion^=to-]:opacity-0 md:absolute motion-reduce:transition-none",
        className,
      )}
      data-slot="navigation-menu-content"
      {...props}
    />
  );
}

function NavigationMenuLink({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Link>) {
  return (
    <NavigationMenuPrimitive.Link
      className={cn(
        "outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
      data-slot="navigation-menu-link"
      {...props}
    />
  );
}

function NavigationMenuIndicator({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Indicator>) {
  return (
    <NavigationMenuPrimitive.Indicator
      className={cn(
        "top-full z-[60] hidden h-3 items-end justify-center overflow-hidden data-[state=hidden]:opacity-0 data-[state=visible]:opacity-100 md:flex",
        className,
      )}
      data-slot="navigation-menu-indicator"
      {...props}
    >
      <span className="relative top-[55%] size-2 rotate-45 border-l border-t border-border bg-surface-100" />
    </NavigationMenuPrimitive.Indicator>
  );
}

function NavigationMenuViewport({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) {
  return (
    <div className="absolute left-0 top-full z-50 flex justify-center">
      <NavigationMenuPrimitive.Viewport
        className={cn(
          "relative mt-3 h-[var(--radix-navigation-menu-viewport-height)] w-[var(--radix-navigation-menu-viewport-width)] overflow-hidden border border-border bg-surface-100 text-foreground shadow-[0_24px_70px_rgba(17,17,17,0.12)]",
          className,
        )}
        data-slot="navigation-menu-viewport"
        {...props}
      />
    </div>
  );
}

NavigationMenu.displayName = "Ui::NavigationMenu";
NavigationMenuList.displayName = "Ui::NavigationMenuList";
NavigationMenuItem.displayName = "Ui::NavigationMenuItem";
NavigationMenuTrigger.displayName = "Ui::NavigationMenuTrigger";
NavigationMenuContent.displayName = "Ui::NavigationMenuContent";
NavigationMenuLink.displayName = "Ui::NavigationMenuLink";
NavigationMenuIndicator.displayName = "Ui::NavigationMenuIndicator";
NavigationMenuViewport.displayName = "Ui::NavigationMenuViewport";

export {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  navigationMenuTriggerClassName,
};
